import { FC, useEffect, useState } from 'react';
import PaymentPaneHeader from './PaymentPaneHeader';
import { EPaymentMode } from '../StudentPaymentPane';
import { Table, TableColumnsType } from 'antd';
import {
  IGetStudentBreakdowns,
  IGetStudentPaymentsDetails,
  IStudentBreakdown,
} from '../../../../models/payments.model';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import useUtil from '../../../../hooks/useUtil';
import useAxios from '../../../../hooks/useAxios';
import useLoader from '../../../../hooks/useLoader';
import { IStudent } from '../../../../models/students.model';
import { getStudentBreakdownsTodo, getStudentPaymentsDetailsTodo } from '../../../../api/payments.api';
import _ from 'lodash';
import PaymentDetailTable from './PaymentDetailTable';
import Button from '../../../Button';
import ChangePaymentModal from './modals/ChangePaymentModal';
import config from '../../../../lib/config';
import Pagination from '../../../common/Pagination';
import PaymentButtons from './PaymentButtons';
import CouponDeleteModal from './modals/CouponDeleteModal';
import { IGetCurrentSemester } from '../../../../models/semesters.model';
import { getCurrentSemester } from '../../../../api/semesters.api';
import PartialRefundModal from './modals/PartialRefundModal';
import { RootState } from '../../../../store/rootReducer';
import { useSelector } from 'react-redux';
import { getWebUrl } from '../../../../lib/util';
import ConfirmModal from '../../../modals/ConfirmModal';
import { cancelPaymentTodo } from '../../../../api/nicePayments.api';
import useNotification from '../../../../hooks/useNotification';

interface IProps {
  student?: IStudent;
  paymentMode: EPaymentMode;
  onSetPaymentMode: (mode: EPaymentMode) => void;
  setAddPaymentVisible: (visible: boolean) => void;
  setDownloadCertificateVisible: (visible: boolean) => void;
  sendLog: (message: string, object?: object) => void;
  setIsReload: (isReload: boolean) => void;
  setTabIndex: (tabIndex: number) => void;
}

const PaymentMade: FC<IProps> = ({
                                   student,
                                   paymentMode,
                                   onSetPaymentMode,
                                   setAddPaymentVisible,
                                   setDownloadCertificateVisible,
                                   sendLog,
                                   setIsReload,
                                   setTabIndex,
                                 }) => {
  const { t } = useTranslation();
  const { getCommonCodeLabel } = useUtil();
  const { successNotification } = useNotification();

  const { data: res, isLoading, error, execute } = useAxios<IGetStudentBreakdowns>();
  const {
    data: detailRes,
    isLoading: detailIsLoading,
    error: detailError,
    execute: detailExecute,
  } = useAxios<IGetStudentPaymentsDetails>();
  const { data: res3, isLoading: isLoading3, execute: execute3 } = useAxios<IGetCurrentSemester>();
  const { data: cancelRes, isLoading: cancelIsLoading, execute: cancelExecute } = useAxios();
  //const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetStudentBreakdownDetails>();

  useLoader(isLoading, [error]);

  const { auth } = useSelector((state: RootState) => state.auth);

  const [data, setData] = useState<IStudentBreakdown[]>([]);
  const [page, setPage] = useState(1);
  const [paymentRecord, setPaymentRecord] = useState<IStudentBreakdown>();
  const [cancelPaymentVisible, setCancelPaymentVisible] = useState(false);
  const [changePaymentMethodVisible, setChangePaymentMethodVisible] = useState(false);
  const [couponUse, setCouponUse] = useState(false);
  const [couponDeleteVisible, setCouponDeleteVisible] = useState(false);
  const [couponDelete, setCouponDelete] = useState(false);
  const [currentSemesterCode, setCurrentSemesterCode] = useState<number>();
  const [partialRefundUse, setPartialRefundUse] = useState(false);
  const [partialRefundVisible, setPartialRefundVisible] = useState(false);
  const [isPlaceTest, setIsPlaceTest] = useState(false);

  useEffect(() => {
    execute3(getCurrentSemester());
  }, []);

  useEffect(() => {
    if (!couponDelete) {
      getStudentPaymentMade();
    } else {
      //쿠폰 취소 처리 완료인 경우
      setPaymentRecord(undefined);
      setCouponDelete(false);
      setIsReload(true);
    }
  }, [student, couponDelete]);

  useEffect(() => {
    if (res?.data) {
      const newData = _.sortBy(
        res.data,
        item => item.paymentDttm,
      ).reverse();
      setData(newData);
      setPage(1);
    }
  }, [res]);

  useEffect(() => {
    //현재 학기 셋팅
    if (res3 && !isLoading3) {
      if (res3?.data?.semesterCode) {
        setCurrentSemesterCode(res3.data.semesterCode);
      }
    }
  }, [res3, isLoading3]);

  useEffect(() => {
    getStudentPaymentMade();
  }, [changePaymentMethodVisible]);

  useEffect(() => {
    if (cancelRes) {
      getStudentPaymentMade();
      setPaymentRecord(undefined);
      successNotification('취소를 완료했습니다.');
    }
  }, [cancelRes]);

  const getStudentPaymentMade = () => {
    if (student?.studentCode) {
      execute(getStudentBreakdownsTodo(student.studentCode));
    }
  };

  useEffect(() => {
    //부분환불 버튼 활성화 여부
    if (!!detailRes?.data && detailRes?.data?.length === 1 && detailRes?.data[0]?.paymentTargetCode === 'PT') {
      setIsPlaceTest(true);
      setPartialRefundUse(true);
      return;
    } else {
      setIsPlaceTest(false);
    }
  }, [detailRes]);

  useEffect(() => {
    if (paymentRecord?.paymentRecordCode) {
      detailExecute(getStudentPaymentsDetailsTodo(paymentRecord.paymentRecordCode));
    }
  }, [paymentRecord]);

  const columns: TableColumnsType<IStudentBreakdown> = [
    {
      title: 'No.',
      dataIndex: 'paymentRecordCode',
    },
    {
      title: t('student.student.88'),
      dataIndex: 'orderName',
    },
    {
      title: t('student.student.column38'),
      render: (value: IStudentBreakdown) =>
        (value.totalPaymentAmount + value.totalDiscountAmount).toLocaleString(),
    },
    {
      title: t('student.student.column39'),
      dataIndex: 'totalDiscountAmount',
      render: (value: number) => value.toLocaleString(),
    },
    {
      title: t('student.student.column40'),
      dataIndex: 'totalPaymentAmount',
      render: (value: number) => value.toLocaleString(),
    },
    {
      title: t('student.student.column41'),
      dataIndex: 'paymentDttm',
      render: (value: string) => moment(value).format('YYYY.MM.DD HH:mm:ss'),
    },
    {
      title: t('student.student.column42'),
      dataIndex: 'paymentMtdCode',
      render: (value) => getCommonCodeLabel('PA0002', value),
    },
    {
      title: t('student.student.column43'),
      key: 'paymentChannelCode',
      dataIndex: 'paymentChannelCode',
      render: (value) => getCommonCodeLabel('PA0004', value),
    },
    {
      title: t('student.student.column44'),
      key: 'memberName',
      dataIndex: 'memberName',
    },
    // {
    //   title: '현금영수증',
    //   render: (value: IStudentBreakdown) => getCashReceipts(value),
    // },
  ];

  const onRowClick = (record: any) => {
    //execute2(getStudentBreakdownDetailsTodo(record.paymentRecordCode)); //결제 상세 정보를 두번가지고 오고 있어 해당 부분 주석 처리 20240702
    setPaymentRecord(record);

    let semesterCode = currentSemesterCode;
    if (!semesterCode) {
      semesterCode = parseInt(String(localStorage.getItem('semesterCode')));
    }

    if (record.totalPaymentAmount === 0 && record.semesterCode >= semesterCode && record.paymentRecordStatus !== 'CC') setCouponUse(true);
    else setCouponUse(false);

    if (auth.memberCode === 'CB00008146' || auth.memberCode === 'CB00015892') {
      if (record.totalPaymentAmount > 0) setPartialRefundUse(true);  //개발자 학기 제한 제거
      else setPartialRefundUse(false);
    } else {
      if (record.totalPaymentAmount > 0 && record.semesterCode >= semesterCode) setPartialRefundUse(true);
      else setPartialRefundUse(false);
    }
  };

  const isWithin90Days = (): boolean => {
    const item = data?.find(it => it?.paymentRecordCode === paymentRecord?.paymentRecordCode);

    if (!item?.paymentDttm) return false; // 데이터가 없거나 firstRegDttm이 없으면 false

    const firstRegDate = new Date(item.paymentDttm); // 등록 날짜
    const today = new Date(); // 현재 날짜
    const diffTime = today.getTime() - firstRegDate.getTime(); // 밀리초 차이
    const diffDays = diffTime / (1000 * 60 * 60 * 24); // 일(day) 단위로 변환

    return diffDays <= 90; // 90일 이내인지 확인
  };

  const onShowReceipt = () => {
    let url = paymentRecord?.receiptUrl ? paymentRecord.receiptUrl : getWebUrl() + '/receipt?uuid=' + paymentRecord?.uuid;

    window.open(url, '_blank');
  };


  const onCancelPayment = () => {
    const paymentRecordCode = paymentRecord?.paymentRecordCode;
    if (paymentRecordCode) {
      cancelExecute(cancelPaymentTodo(paymentRecordCode));
      setCancelPaymentVisible(false);
    }
  };

  return (
    <>
      <PaymentPaneHeader
        paymentMode={paymentMode}
        onSetPaymentMode={onSetPaymentMode}
        setAddPaymentVisible={setAddPaymentVisible}
        setDownloadCertificateVisible={setDownloadCertificateVisible}
      />
      <Table
        className="comm-table__area"
        columns={columns}
        dataSource={data}
        pagination={{
          current: page,
          pageSize: config.pageSize,
        }}
        onRow={(record) => ({
          onClick: () => onRowClick(record),
        })}
        rowClassName={(record) =>
          record === paymentRecord ? 'chosen' : 'pointer' + (record.paymentRecordStatus === 'CC' ? ' table-row-bg' : '')
        }
      />
      <div className="comm-bx__between">
        <div />
        <div className="common-paging center">
          <Pagination page={page} setPage={setPage} total={data.length} />
        </div>
        <div className="comm-btn__area right">
          <PaymentButtons
            setAddPaymentVisible={setAddPaymentVisible}
            onSetPaymentMode={onSetPaymentMode}
          />
        </div>
      </div>
      {paymentRecord && (
        <>
          <PaymentDetailTable paymentRecordDetails={detailRes?.data ?? []} />
          <div className="comm-bx__between">
            <div className="comm-btn__area">
              {((paymentRecord?.pgCompany === 'TOSS' && !!paymentRecord?.receiptUrl) ||
                (paymentRecord?.pgCompany === 'NICE' && !!paymentRecord?.uuid && paymentRecord?.paymentMethodDetailCode !== '03')) &&
                (
                  <Button
                    className="btn-basic pink-line"
                    text={t('student.student.button16')}
                    onClick={onShowReceipt}
                  />
                )}
            </div>
            <div className="comm-btn__area right">
              {couponUse && (
                <Button
                  className="btn-basic gray-line"
                  text={t('student.student.button27')}
                  onClick={() => setCouponDeleteVisible(true)}
                />
              )}

              {(partialRefundUse && paymentRecord?.refundPsbYn === 'Y' && paymentRecord?.paymentMethodDetailCode !== '03') && (
                <Button
                  className="btn-basic pink-line"
                  text={t('student.student.button28')}
                  onClick={() => setPartialRefundVisible(true)}
                />
              )}

              {isWithin90Days() && paymentRecord?.paymentMethodDetailCode !== '03' && (
                <Button
                  className="btn-basic pink-line"
                  text={t('결제 카드 변경')}
                  onClick={() => setChangePaymentMethodVisible(true)}
                  disabled={paymentRecord.paymentRecordStatus === 'CG'}
                />
              )}

              {paymentRecord?.paymentMethodDetailCode === '03' && (
                <Button
                  className="btn-basic pink-line"
                  text={t('결제 취소')}
                  onClick={() => setCancelPaymentVisible(true)}
                />
              )}
            </div>
          </div>
        </>
      )}

      {cancelPaymentVisible && (
        <ConfirmModal
          visible={cancelPaymentVisible}
          setVisible={setCancelPaymentVisible}
          onConfirm={onCancelPayment}
          titleText={t('관리자 결제 취소')}
          messageText={t('관리자 결제건을 취소하시겠습니까?')}
          confirmText={t('common.button.confirm')}
          cancelText={t('common.button.cancel')}
        />
      )}


      {changePaymentMethodVisible && (
        <ChangePaymentModal
          visible={changePaymentMethodVisible}
          setVisible={setChangePaymentMethodVisible}
          student={student}
          paymentRecord={paymentRecord}
          setPaymentRecord={setPaymentRecord}
          getStudentPaymentMade={getStudentPaymentMade}
          sendLog={sendLog}
        />
      )}

      {couponDeleteVisible && (
        <CouponDeleteModal
          visible={couponDeleteVisible}
          setVisible={setCouponDeleteVisible}
          student={student}
          paymentRecord={paymentRecord}
          setPaymentRecord={setPaymentRecord}
          couponDelete={couponDelete}
          setCouponDelete={setCouponDelete}
        />
      )}

      {partialRefundVisible && (
        <PartialRefundModal
          visible={partialRefundVisible}
          setVisible={setPartialRefundVisible}
          paymentRecord={paymentRecord}
          setTabIndex={setTabIndex}
          isPlaceTest={isPlaceTest}
        />
      )}

    </>
  );
};

export default PaymentMade;
